(() => {
  "use strict";

  const runtimeKey = "__guruTrustpilotReviews";
  const rootSelector = "[data-guru-trustpilot]";
  const existingRuntime = window[runtimeKey];

  if (existingRuntime?.scan) {
    existingRuntime.scan(document);
    return;
  }

  const controllers = new Map();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(rootSelector)) roots.push(scope);
    scope?.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const initialize = (root) => {
    if (!root.isConnected || controllers.has(root)) return;
    const viewport = root.querySelector("[data-guru-trustpilot-viewport]");
    const track = root.querySelector("[data-guru-trustpilot-track]");
    const group = root.querySelector("[data-guru-trustpilot-group]");
    if (!viewport || !track || !group || group.children.length === 0) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    let clone = null;
    let layoutFrame = 0;
    let observedWidth = -1;
    let resizeObserver = null;

    const removeClone = () => {
      clone?.remove();
      clone = null;
    };

    const ensureClone = () => {
      if (clone || reducedMotion.matches || group.children.length < 2 || signal.aborted) return;
      clone = group.cloneNode(true);
      clone.removeAttribute("data-guru-trustpilot-group");
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
      clone.querySelectorAll("a[href], button, input, select, textarea, [tabindex]").forEach((element) => element.setAttribute("tabindex", "-1"));
      track.append(clone);
    };

    const sync = () => {
      window.cancelAnimationFrame(layoutFrame);
      track.classList.remove("is-running");
      if (signal.aborted || reducedMotion.matches || group.children.length < 2) {
        removeClone();
        track.style.removeProperty("--guru-trustpilot-distance");
        track.style.removeProperty("--guru-trustpilot-duration");
      }

      layoutFrame = window.requestAnimationFrame(() => {
        if (signal.aborted) return;
        const gap = Number.parseFloat(getComputedStyle(group).columnGap) || 0;
        const mobile = window.matchMedia("(max-width: 600px)").matches;
        const tablet = window.matchMedia("(max-width: 900px)").matches;
        const availableWidth = Math.max(1, viewport.clientWidth - (mobile ? 16 : 0));
        const cardWidth = mobile
          ? availableWidth * 0.88
          : tablet
            ? (availableWidth - gap) / 2
            : (availableWidth - (gap * 2)) / 3;
        track.style.setProperty("--guru-trustpilot-card-width", `${cardWidth}px`);

        if (reducedMotion.matches || group.children.length < 2) return;
        ensureClone();
        layoutFrame = window.requestAnimationFrame(() => {
          if (signal.aborted || reducedMotion.matches || !clone) return;
          const distance = group.getBoundingClientRect().width;
          if (!Number.isFinite(distance) || distance <= 0) return;
          track.style.setProperty("--guru-trustpilot-distance", `${-distance}px`);
          track.style.setProperty("--guru-trustpilot-duration", `${Math.max(72, distance / 24)}s`);
          track.classList.add("is-running");
        });
      });
    };

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === observedWidth) return;
        observedWidth = width;
        sync();
      });
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener("resize", sync, { signal });
    }

    reducedMotion.addEventListener?.("change", sync, { signal });
    document.fonts?.ready.then(() => {
      if (!signal.aborted) sync();
    });

    const destroy = () => {
      abortController.abort();
      window.cancelAnimationFrame(layoutFrame);
      resizeObserver?.disconnect();
      track.classList.remove("is-running");
      removeClone();
      controllers.delete(root);
    };

    controllers.set(root, { destroy });
    sync();
  };

  const scan = (scope = document) => rootsWithin(scope).forEach(initialize);
  const cleanup = (scope) => {
    [...controllers].forEach(([root, controller]) => {
      if (root === scope || scope?.contains?.(root) || !root.isConnected) controller.destroy();
    });
  };

  const onSectionLoad = (event) => scan(event.target);
  const onSectionUnload = (event) => cleanup(event.target);
  document.addEventListener("shopify:section:load", onSectionLoad);
  document.addEventListener("shopify:section:unload", onSectionUnload);

  const runtime = { scan, cleanup };
  window[runtimeKey] = runtime;
  scan(document);
})();
